/**
 * Azure AD B2C Authentication Service
 *
 * This service handles all B2C authentication operations using MSAL.js
 */

import * as msal from '@azure/msal-browser'
import axios from 'axios'
import { buildMsalConfig, buildAuthScopes } from './b2cConfig'

class B2CAuthService {
  constructor() {
    this.msalInstance = null
    this.account = null
    this.scopes = null
  }

  /**
   * Fetch the anonymous auth configuration from /AuthConfig/v2.
   * Returns the proper auth settings (client id, scope, authority/policy)
   * so the redirect uri can be determined at runtime.
   */
  async fetchAuthConfig() {
    const response = await axios.get('/AuthConfig/v2')
    return response.data
  }

  /**
   * Initialize MSAL instance
   */
  async initialize() {
    const authConfig = await this.fetchAuthConfig()
    const scopes = buildAuthScopes(authConfig)
    this.msalConfig = buildMsalConfig(authConfig)
    this.scopes = scopes

    this.msalInstance = new msal.PublicClientApplication(this.msalConfig)
    await this.msalInstance.initialize()

    // Handle redirect promise
    try {
      const response = await this.msalInstance.handleRedirectPromise()
      if (response) {
        this.account = response.account
        this.msalInstance.setActiveAccount(response.account)
        return response
      }
    } catch (error) {
      console.error('Error handling redirect:', error)
      throw error
    }

    // Check if there are any accounts already signed in
    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      this.account = accounts[0]
    }

    return null
  }

  /**
   * Get all accounts
   */
  getAllAccounts() {
    if (!this.msalInstance) {
      return []
    }
    return this.msalInstance.getAllAccounts()
  }

  /**
   * Get current account
   */
  getAccount() {
    return this.account
  }

  /**
   * Set active account
   */
  setAccount(account) {
    this.account = account
    if (this.msalInstance) {
      this.msalInstance.setActiveAccount(account)
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const accounts = this.getAllAccounts()
    return accounts.length > 0
  }

  /**
   * Whether MSAL has been initialized and a signed-in account exists.
   */
  isReady() {
    return !!this.msalInstance && this.isAuthenticated()
  }

  /**
   * Sign in using redirect
   */
  async signInRedirect() {
    try {
      const loginRequest = { scopes: this.scopes }
      await this.msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login redirect error:', error)
      throw error
    }
  }

  /**
   * Sign out
   */
  async signOut() {
    const logoutRequest = {
      account: this.account,
      postLogoutRedirectUri: this.msalConfig.auth.postLogoutRedirectUri
    }

    try {
      await this.msalInstance.logoutRedirect(logoutRequest)
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }

  /**
   * Get access token silently
   */
  async acquireTokenSilent() {
    if (!this.account) {
      throw new Error('No active account. Please sign in.')
    }

    const request = {
      scopes: this.scopes,
      forceRefresh: false,
      account: this.account
    }

    try {
      let response = await this.msalInstance.acquireTokenSilent(request)

      if (!response.accessToken) {
        throw new Error('No access token returned from silent acquisition.')
      }

      if (response.expiresOn && response.expiresOn <= new Date()) {
        response = await this.msalInstance.acquireTokenSilent({ ...request, forceRefresh: true })

        if (!response.accessToken) {
          throw new Error('No access token returned after forced refresh.')
        }
      }

      return response.accessToken
    } catch (error) {
      console.warn('Silent token acquisition failed:', error)

      // If silent token acquisition fails, try to acquire token using redirect
      if (error instanceof msal.InteractionRequiredAuthError) {
        await this.msalInstance.acquireTokenRedirect(request)
      } else {
        throw error
      }
    }
  }

  /**
   * Get access token using redirect (for interactive scenarios)
   */
  async acquireTokenRedirect() {
    const request = {
      scopes: this.scopes,
      forceRefresh: false,
      account: this.account
    }

    try {
      await this.msalInstance.acquireTokenRedirect(request)
    } catch (error) {
      console.error('Token acquisition redirect error:', error)
      throw error
    }
  }

  /**
   * Get user info from account
   */
  getUserInfo() {
    if (!this.account) {
      return null
    }

    return {
      username: this.account.username,
      name: this.account.name,
      email: this.account.username,
      idTokenClaims: this.account.idTokenClaims
    }
  }
}

// Export a singleton instance
export default new B2CAuthService()
